import throttle from 'lodash/throttle'
import isEqual from 'lodash/isEqual'
import authType from '@/store/type/authType'
import { ROLE_ADMIN } from '@/enum/roles'
import selectapiType from '@/store/type/selectapiType'
import Config from '@/config.loader'
import { firebaseAuth, firebaseDb } from '@/plugins/firebase'

const UPD_TIMEOUT = 700
const restaurantGetter = `${selectapiType.getModuleName(
    selectapiType.clientTypes.TABLET_RESTAURANTS
)}/${selectapiType.getters.VALUES}`
const userGetter = `${selectapiType.getModuleName(
    selectapiType.clientTypes.TABLET_USERS
)}/${selectapiType.getters.VALUES}`
let timeout = null
// tablet id -> { parts, item } of the last tabletClients run: an unchanged tablet keeps its row object
let clientRows = new Map()

const subscribeToFirebaseTopic = (db, topic, commit, dispatch, mutation) => {
    const throttledCommit = throttle(
        (snapshot) => {
            const data = snapshot.val()
            dispatch(mutation, data)
            console.log(`FB throttled ${mutation}`, new Date().getTime())
        },
        3000,
        {
            leading: true,
            trailing: true,
        }
    )

    db.ref(topic).on('value', (snapshot) => {
        try {
            throttledCommit(snapshot)
            // if (mutation === 'setClientConfigs') throttledLookup()
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(`Failed subscribing ${topic} to topic`)
        }
    })
}

const dbRoute = Config.getConfigValue('VUE_APP_FB_DB_ROUTE')

const updateAction = (prop, mutation, { commit, state }, data) => {
    const updated = []
    for (let key in state[prop]) {
        if (!isEqual(state[prop][key], data[key])) updated.push(key)
    }
    // every Firebase snapshot is a new object even when nothing changed: an unchanged one is not
    // committed, and neither is an unchanged "updated" list (each commit re-rendered the whole tablet
    // table, ~0.4 s for 173 tablets on prod; tasks#211)
    if (
        updated.length ||
        !isEqual(Object.keys(state[prop] || {}), Object.keys(data || {}))
    )
        commit(mutation, data)
    if (!isEqual(state.updated, updated)) commit('setUpdated', updated)
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => {
        if (state.updated.length) commit('setUpdated', [])
    }, UPD_TIMEOUT)
}

export default {
    namespaced: true,
    state: {
        isInited: false,
        isSubscribed: false,
        clientConfigs: {},
        clientStatuses: {},
        clientSunmis: {},
        updated: [],
        users: {},
        restaurants: {},
    },

    mutations: {
        setInited: (state, v) => (state.isInited = v),

        setSubscribed: (state, v) => (state.isSubscribed = v),

        setClientConfigs: (state, data) => {
            const updated = []
            for (let key in state.clientConfigs) {
                if (!isEqual(state.clientConfigs[key], data[key]))
                    updated.push(key)
            }
            state.clientConfigs = data
        },

        setClientStatuses: (state, data) => {
            const updated = []
            for (let key in state.clientStatuses) {
                if (!isEqual(state.clientStatuses[key], data[key]))
                    updated.push(key)
            }
            state.clientStatuses = data
        },

        setClientSunmis: (state, data) => {
            const updated = []
            for (let key in state.clientSunmis) {
                if (!isEqual(state.clientSunmis[key], data[key]))
                    updated.push(key)
            }
            state.clientSunmis = data
        },

        setUpdated: (state, data) => (state.updated = data),

        setUsers: (state, data) => (state.users = data),

        setRestaurants: (state, data) => (state.restaurants = data),
    },

    actions: {
        async init({ state, commit, dispatch, rootGetters }) {
            const restaurantsTask = dispatch(
                selectapiType.getActionName(
                    selectapiType.clientTypes.TABLET_RESTAURANTS,
                    selectapiType.actions.LOAD
                ),
                null,
                { root: true }
            )
            const usersTask = dispatch(
                selectapiType.getActionName(
                    selectapiType.clientTypes.TABLET_USERS,
                    selectapiType.actions.LOAD
                ),
                null,
                { root: true }
            )
            await Promise.allSettled([restaurantsTask, usersTask])

            const users = {}
            for (let user of rootGetters[userGetter]({})) {
                users[user.id] = user
            }
            commit('setUsers', users)
            const restaurants = {}
            for (let restaurant of rootGetters[restaurantGetter]({})) {
                restaurants[restaurant.id] = restaurant
            }
            commit('setRestaurants', restaurants)

            if (state.isInited) return dispatch('subscribe')

            if (
                rootGetters[authType.getters.GET_ROLES] &&
                Array.isArray(rootGetters[authType.getters.GET_ROLES]) &&
                rootGetters[authType.getters.GET_ROLES].includes(ROLE_ADMIN)
            ) {
                if (await dispatch('login')) {
                    await dispatch('subscribe')
                    commit('setInited', true)
                }
            }
        },

        // @TODO: Extend this, remove hardcoded user, solve user sync with core-api
        async login() {
            if (process.env.VUE_APP_FB_U && process.env.VUE_APP_FB_P)
                return await (await firebaseAuth())
                    .signInWithEmailAndPassword(
                        process.env.VUE_APP_FB_U,
                        process.env.VUE_APP_FB_P
                    )
                    .catch((e) => {
                        // eslint-disable-next-line no-console
                        console.warn('Firebase Auth Failed', e)
                    })
                    .then(() => true)
        },

        async subscribe({ commit, state, dispatch }) {
            if (state.isSubscribed) return

            const db = await firebaseDb()
            if (state.isSubscribed) return
            subscribeToFirebaseTopic(
                db,
                `${dbRoute}/status`,
                commit,
                dispatch,
                'setClientStatuses'
            )
            subscribeToFirebaseTopic(
                db,
                `${dbRoute}/clients`,
                commit,
                dispatch,
                'setClientConfigs'
            )
            subscribeToFirebaseTopic(
                db,
                `${dbRoute}/sunmiv2`,
                commit,
                dispatch,
                'setClientSunmis'
            )
            commit('setSubscribed', true)
        },

        async unsubscribe({ commit, state }) {
            if (!state.isSubscribed) return

            const db = await firebaseDb()
            db.ref(`${dbRoute}/status`).off()
            db.ref(`${dbRoute}/clients`).off()
            db.ref(`${dbRoute}/sunmiv2`).off()
            commit('setSubscribed', false)
        },

        async setConfig(_vuex, params = {}) {
            if (params && params.id && params.key) {
                return await (await firebaseDb())
                    .ref(`${dbRoute}/clients/${params.id}/${params.key}`)
                    .set(params.value)
            }
        },

        async deleteConfig(_vuex, id) {
            if (id) {
                const db = await firebaseDb()
                return Promise.allSettled([
                    db.ref(`${dbRoute}/clients/${id}`).remove(),
                    db.ref(`${dbRoute}/status/${id}`).remove(),
                    db.ref(`${dbRoute}/sunmi/${id}`).remove(),
                ])
            }
        },

        setClientConfigs: (_vuex, data) => {
            updateAction('clientConfigs', 'setClientConfigs', _vuex, data)
        },

        setClientStatuses: (_vuex, data) => {
            updateAction('clientStatuses', 'setClientStatuses', _vuex, data)
        },

        setClientSunmis: (_vuex, data) => {
            updateAction('clientSunmis', 'setClientSunmis', _vuex, data)
        },
    },

    getters: {
        tabletClients: (state) => {
            const rows = new Map()
            const clients = Object.keys(state.clientStatuses || {}).map(
                (el) => {
                    const clientConf = state.clientConfigs[el]
                    const parts = {
                        status: state.clientStatuses[el],
                        clientConf,
                        sunmi: state.clientSunmis[el],
                        updated: state.updated.includes(el),
                        user: state.users[clientConf?.user_id],
                        restaurants: state.restaurants,
                    }
                    // the same row object while nothing of this tablet changed, so its cells are not rebuilt
                    const previous = clientRows.get(el)
                    if (
                        previous &&
                        previous.parts.status === parts.status &&
                        previous.parts.updated === parts.updated &&
                        previous.parts.user === parts.user &&
                        previous.parts.restaurants === parts.restaurants &&
                        isEqual(previous.parts.clientConf, parts.clientConf) &&
                        isEqual(previous.parts.sunmi, parts.sunmi)
                    ) {
                        rows.set(el, previous)
                        return previous.item
                    }
                    const restaurants = []
                    for (let id of parts.user?.restaurants || []) {
                        if (state.restaurants[id])
                            restaurants.push(state.restaurants[id])
                    }
                    const item = {
                        id: el,
                        status: parts.status,
                        user: parts.user,
                        restaurants,
                        updated: parts.updated,
                        ...(clientConf || {}),
                        sunmi: parts.sunmi,
                    }
                    rows.set(el, { parts, item })
                    return item
                }
            )
            clientRows = rows
            return clients
        },

        // non-Deliverect restaurant id -> the tablet clients logged in with one of its users
        restaurantTablets: (_state, getters) => {
            const result = {}
            for (const client of getters.tabletClients) {
                for (const restaurant of client.restaurants) {
                    if (restaurant.isDeliverect) continue
                    if (!result[restaurant.id]) result[restaurant.id] = []
                    result[restaurant.id].push(client)
                }
            }
            return result
        },
    },
}
